"use client";

/**
 * LiveCalls — agency-wide live call monitoring console (Listen + Whisper).
 *
 * REDESIGN (2026-07-24): split console. Left = call roster grouped by agency,
 * searchable. Right = sticky monitor rail that never scrolls away while you're
 * connected. Replaces the flat card list, which gave no indication of WHICH
 * agency a call belonged to — a real problem now that the backend correctly
 * surfaces the full descendant tree (up to 5 levels deep on prod today).
 *
 *   ┌─ roster ─────────────────┬─ monitor (sticky) ──┐
 *   │ ▾ OPERATION EL      (5)  │  ◉ LISTENING 02:14  │
 *   │   ◉ K.Korol → M.Diaz     │  ∿∿╱╲∿∿╱╲∿∿        │
 *   │ ▾ DUSE AGENCY       (4)  │  vol ──────◉──      │
 *   │   ◉ H.Quraish → T.Bell   │  [CLICK TO TALK]    │
 *   └──────────────────────────┴─────────────────────┘
 *
 * BARGE IS GONE (2026-07-23 owner decision) — replaced by agent-initiated Live
 * Transfer to owners. All barge state/handlers/dialogs were removed here rather
 * than left dead; the backend endpoints still exist but nothing calls them.
 *
 * Audio lives in useCoachAudioEngine — this file owns REST/socket/UI only.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useRouter } from "next/navigation";
import {
  Phone, PhoneOff, Headphones, Radio, Clock, User, Loader2, Volume2, Mic,
  ExternalLink, Eye, EyeOff, Search, ChevronDown, ChevronRight, EyeIcon, Building2,
} from "lucide-react";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { showToast } from "@/app/_module/utils/show-toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useCoachSession } from "@/app/_module/contexts/CoachSessionContext";
import CoachWaveform from "./CoachWaveform";
import CoachAudioControls from "./CoachAudioControls";
import { acquireSharedChannel } from "@/app/_module/services/sharedRealtimeChannel";
import { useRealtimeRejoin } from "@/app/_module/services/realtimeHealth";

/** Barge removed — the console only ever runs in these two modes. */
type CoachMode = "listen" | "whisper";

interface ActiveCallListener {
  userId: string;
  name: string;
  avatar: string | null;
  joinedAt: string;
}

interface ActiveCall {
  id: string;
  callSid: string;
  userId: string;
  leadId: string;
  callType: string;
  callStatus: string;
  contactName: string;
  contactNumber: string;
  createdAt: string;
  duration: number;
  agentName: string;
  agentEmail: string;
  agentAvatar: string;
  leadName: string;
  leadPhone: string;
  /** Agency the call belongs to — may be a sub or sub-sub agency of the viewer. */
  agencyId?: string;
  agencyName?: string;
  /** 'multi_line' | 'power_dialer' | 'manual' — same labels as Recent Activity. */
  dialerMode?: string;
  /** Currently-listening managers (from gateway state) — manager-only data. */
  listeners?: ActiveCallListener[];
  listenerCount?: number;
}

interface EavesdropState {
  isListening: boolean;
  callSid: string | null;
  streamSid: string | null;
  sessionId: string | null;
  mode: CoachMode;
  agentName: string;
  leadName: string;
  wsConnection: WebSocket | null;
  audioContext: AudioContext | null;
  startedAt: Date | null;
}

/** (xxx) xxx-xxxx for display; unchanged if fewer than 10 digits. */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (ten.length !== 10) return raw;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

/** Dial-mode chip styling. Vocabulary matches the lead-details Recent Activity
 *  timeline so a supervisor sees one consistent label for the same call. */
const DIAL_MODE_CHIP: Record<string, { label: string; cls: string }> = {
  multi_line:   { label: "Multi-line",   cls: "bg-violet-50 text-violet-700 border-violet-200" },
  power_dialer: { label: "Power dialer", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  manual:       { label: "Manual",       cls: "bg-slate-50 text-slate-600 border-slate-200" },
};

const UNGROUPED = "Your agency";

export default function LiveCalls() {
  const router = useRouter();
  const { user } = useUserContext();

  // Live session state/actions come from the app-wide provider — NOT local state,
  // so switching tabs no longer tears the session down.
  const {
    isListening, callSid: activeCallSid, mode, agentName, leadName,
    duration: listenDuration, volume, setVolume, inboundLevel,
    micActive, micVolume, micLatched, followingAgentId,
    start: startSession, stop: stopSession, toggleMic, readSpectrum,
    followAgent, unfollowAgent, isStarting,
  } = useCoachSession();

  const [activeCalls, setActiveCalls] = useState<ActiveCall[]>([]);

  // Bumped when the realtime channel dies so the effect re-subscribes.

  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const pollIntervalRef = useRef<ReturnType<typeof setInterval>>(undefined);

  // ── Data ───────────────────────────────────────────────────────────────────
  const fetchActiveCalls = useCallback(async () => {
    try {
      const response = await api.get<any>({
        url: getApiUrl("/communication/eavesdrop/active-calls"),
        auth: true,
      });
      const data = response?.data?.data || response?.data || [];
      setActiveCalls(Array.isArray(data) ? data : []);
    } catch {
      // Silently fail — will retry on next poll
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 10s poll as the floor + Supabase broadcast for instant updates.
  // Guarded rejoin: backoff + attempt cap (an unguarded retry loop hangs the tab).
  const { rejoinToken, handleStatus } = useRealtimeRejoin("agency-live-calls");
  useEffect(() => {
    fetchActiveCalls();
    pollIntervalRef.current = setInterval(fetchActiveCalls, 10000);

    const agencyId = (user?.currentAgency as any)?.agencyId || (user?.currentAgency as any)?.agency?.id;

    // DEAD-CLIENT FIX (2026-08-12): built its own client from NEXT_PUBLIC_SUPABASE_URL
    // / _ANON_KEY, which are UNSET here (the app decodes NEXT_PUBLIC_SB_U / SB_K), so
    // this always returned early and the live-call roster ran on its 10s poll alone.
    //
    // ⚠️ SHARED CHANNEL (2026-08-13): the Hierarchy tab subscribes to this exact topic
    // and can be mounted at the same time. Two owners of one channel object meant the
    // first to unmount tore it down under the other. See sharedRealtimeChannel.ts.
    //
    // The backend fans these out to the agent's agency AND every ancestor agency
    // (broadcastAgencyLiveCallEventUpTree), so an upline owner gets sub / sub-sub-agency
    // calls instantly instead of waiting for the poll. `call_started` is deliberately
    // NOT subscribed: the active-calls query only surfaces connected, real-CallSid rows
    // (pre-answer legs have no audio to coach), so a start event can never return
    // anything new.
    const handle = agencyId
      ? acquireSharedChannel(
          `agency-live-calls:${agencyId}`,
          {
            call_answered: () => fetchActiveCalls(),
            call_ended: () => fetchActiveCalls(),
          },
          handleStatus,
        )
      : null;

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      handle?.release();
    };
  }, [fetchActiveCalls, user, rejoinToken]);

  // ── Roster grouping / search ───────────────────────────────────────────────
  // Grouping by agency is the whole point of the redesign: the backend returns
  // the full descendant tree, so without this an owner sees a flat list of
  // strangers with no idea which downline agency they belong to.
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? activeCalls.filter((c) =>
          [c.agentName, c.leadName, c.contactName, c.contactNumber, c.agencyName]
            .some((f) => (f || "").toLowerCase().includes(q)),
        )
      : activeCalls;

    const byAgency = new Map<string, ActiveCall[]>();
    for (const call of filtered) {
      const key = call.agencyName || UNGROUPED;
      if (!byAgency.has(key)) byAgency.set(key, []);
      byAgency.get(key)!.push(call);
    }
    // Own agency first, then busiest downline agencies.
    return Array.from(byAgency.entries()).sort((a, b) => {
      if (a[0] === UNGROUPED) return -1;
      if (b[0] === UNGROUPED) return 1;
      if (b[1].length !== a[1].length) return b[1].length - a[1].length;
      return a[0].localeCompare(b[0]);
    });
  }, [activeCalls, query]);

  // Session lifecycle (socket + audio + mic) lives in CoachSessionProvider so
  // it survives navigating to other tabs. This component is now purely the
  // roster + monitor UI.

  // ── Formatting ─────────────────────────────────────────────────────────────
  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const getCallAge = (createdAt: string) => {
    const seconds = Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  };

  const isWhisper = mode === "whisper";
  const totalShown = groups.reduce((n, [, calls]) => n + calls.length, 0);

  return (
    <div>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold tracking-tight text-gray-900">
            <Radio className="h-4 w-4 text-red-500" />
            Live Calls
          </h2>
          <p className="mt-0.5 text-sm text-gray-500">
            Monitor active calls across your agency and every sub-agency, in real time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {activeCalls.length > 0 && (
            <Badge variant="outline" className="animate-pulse border-green-200 bg-green-50 text-green-700">
              {activeCalls.length} active
            </Badge>
          )}
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search calls…"
              aria-label="Search live calls"
              className="h-9 w-full min-w-[240px] rounded-md border border-gray-200 bg-white pl-8 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15"
            />
          </div>
        </div>
      </div>

      {/* ── Split console: roster + sticky monitor ──────────────────────────── */}
      <div className={`grid gap-5 ${isListening ? "lg:grid-cols-[minmax(0,1fr)_340px]" : "grid-cols-1"}`}>
        {/* Roster */}
        <div className="min-w-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : activeCalls.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 py-16 text-center">
              <Phone className="mx-auto mb-3 h-10 w-10 text-gray-200" />
              <p className="text-sm font-medium text-gray-500">No active calls right now</p>
              <p className="mt-1 text-xs text-gray-400">
                Calls from your agency and sub-agencies appear here the moment they connect
              </p>
            </div>
          ) : totalShown === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 py-14 text-center">
              <Search className="mx-auto mb-3 h-8 w-8 text-gray-200" />
              <p className="text-sm font-medium text-gray-500">No calls match “{query}”</p>
              <button onClick={() => setQuery("")} className="mt-2 text-xs text-primary hover:underline">
                Clear search
              </button>
            </div>
          ) : (
            /* SCROLL THE ROSTER, DON'T GROW THE PAGE. With 100 concurrent calls
               an unbounded list pushed the dashboard down "like crazy" and
               scrolled the sticky monitor rail out of reach. Capping the height
               keeps the rail and the search box on screen while the list scrolls
               inside itself. -22rem ≈ page header + section header + search row;
               min-h keeps it usable on short viewports. `overscroll-contain`
               stops the page from scrolling once the list hits its end. */
            <div className="max-h-[calc(100vh-22rem)] min-h-[18rem] space-y-4 overflow-y-auto overscroll-contain pr-1">
              {groups.map(([agencyName, calls]) => {
                const isCollapsed = !!collapsed[agencyName];
                return (
                  <div key={agencyName}>
                    {/* Agency header — the thing the old flat list was missing */}
                    <button
                      onClick={() => setCollapsed((p) => ({ ...p, [agencyName]: !p[agencyName] }))}
                      /* Sticks to the top of the scroller so you always know
                         which agency the rows below belong to — matters once the
                         list is long enough to scroll. z-10 keeps it above rows;
                         the bg is opaque so rows don't show through. */
                      className="sticky top-0 z-10 -mx-1 mb-2 flex w-full items-center gap-1.5 bg-white/95 px-1 py-1 text-left backdrop-blur-sm"
                      aria-expanded={!isCollapsed}
                    >
                      {isCollapsed
                        ? <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                        : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
                      <Building2 className="h-3 w-3 text-gray-400" />
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                        {agencyName}
                      </span>
                      <span className="text-[11px] text-gray-400">({calls.length})</span>
                      <span className="ml-2 h-px flex-1 bg-gray-100" />
                    </button>

                    {!isCollapsed && (
                      <div className="space-y-2">
                        {calls.map((call) => {
                          const isActive = activeCallSid === call.callSid;
                          return (
                            <div
                              key={call.callSid}
                              className={`rounded-lg border p-3 transition-all ${
                                isActive
                                  ? "border-primary/40 bg-primary/5 shadow-sm ring-1 ring-primary/10"
                                  : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                {/* Agent */}
                                <div className="relative shrink-0">
                                  {call.agentAvatar ? (
                                    <img
                                      src={call.agentAvatar}
                                      alt={call.agentName}
                                      className="h-9 w-9 rounded-full border border-gray-200 object-cover"
                                    />
                                  ) : (
                                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                                      <User className="h-4 w-4 text-primary" />
                                    </div>
                                  )}
                                  <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-green-500" />
                                </div>

                                {/* Identity + lead */}
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className="truncate text-sm font-semibold text-gray-900">{call.agentName}</p>
                                    <span className="inline-flex items-center gap-1 font-mono text-[11px] tabular-nums text-gray-500">
                                      <Clock className="h-3 w-3 text-gray-300" />
                                      {getCallAge(call.createdAt)}
                                    </span>
                                    {(() => {
                                      // No stamp and no session link → the
                                      // backend sends null. Render NO chip
                                      // rather than asserting "Manual", which
                                      // was wrong for 266 of 267 live calls.
                                      const chip = call.dialerMode
                                        ? DIAL_MODE_CHIP[call.dialerMode]
                                        : undefined;
                                      if (!chip) return null;
                                      return (
                                        <Badge variant="outline" className={`px-1.5 py-0 text-[10px] ${chip.cls}`}>
                                          {chip.label}
                                        </Badge>
                                      );
                                    })()}
                                    {isActive && (
                                      <Badge variant="outline" className="border-primary/30 bg-primary/10 px-1.5 py-0 text-[10px] uppercase tracking-wide text-primary">
                                        {mode}
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                    <p className="truncate text-xs text-gray-500">
                                      → {call.leadName || call.contactName || "Unknown"}
                                    </p>
                                    {call.contactNumber && (
                                      <span className="font-mono text-[11px] text-gray-400">
                                        {formatPhone(call.contactNumber)}
                                      </span>
                                    )}
                                    {call.leadId && (
                                      // Reads as a button, not a text link — it navigates away
                                      // from a live-monitoring view, so it should look
                                      // deliberate. stopPropagation because the whole row is
                                      // clickable (row click starts listening).
                                      // `group/lead` is NAMED so the icon nudge can't be
                                      // triggered by hovering any ancestor group.
                                      <button
                                        onClick={(e) => { e.stopPropagation(); router.push(`/leads/${call.leadId}`); }}
                                        className="group/lead inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] font-medium leading-none text-gray-600 shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-all duration-150 hover:-translate-y-px hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 hover:shadow-[0_2px_6px_rgba(79,70,229,0.15)] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/50 active:translate-y-0 active:scale-[0.97]"
                                        title="Open lead details"
                                        aria-label={`Open lead details for ${call.leadName || call.contactName || "this lead"}`}
                                      >
                                        <ExternalLink className="h-2.5 w-2.5 transition-transform duration-150 group-hover/lead:-translate-y-px group-hover/lead:translate-x-px" />
                                        View Lead
                                      </button>
                                    )}
                                  </div>

                                  {/* Other managers already on this call */}
                                  {call.listeners && call.listeners.length > 0 && (
                                    <div className="mt-1.5 flex items-center gap-1.5">
                                      <EyeIcon className="h-2.5 w-2.5 text-gray-300" />
                                      <div className="flex -space-x-1.5">
                                        {call.listeners.slice(0, 3).map((l) => (
                                          <div
                                            key={l.userId}
                                            className="flex h-5 w-5 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-indigo-100"
                                            title={`${l.name} — joined ${new Date(l.joinedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}
                                          >
                                            {l.avatar ? (
                                              <img src={l.avatar} alt={l.name} className="h-full w-full object-cover" />
                                            ) : (
                                              <span className="text-[9px] font-bold text-indigo-700">
                                                {l.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                                              </span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                      {call.listeners.length > 3 && (
                                        <span className="text-[10px] text-gray-400">+{call.listeners.length - 3}</span>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* Actions */}
                                <div className="flex shrink-0 items-center gap-1.5">
                                  {isActive ? (
                                    <>
                                      {followingAgentId === call.userId ? (
                                        <Button size="sm" variant="ghost" onClick={unfollowAgent} className="gap-1.5 text-xs text-blue-600" title="Stop auto-following this agent">
                                          <EyeOff className="h-3.5 w-3.5" />
                                          Unfollow
                                        </Button>
                                      ) : (
                                        <Button size="sm" variant="ghost" onClick={() => followAgent(call)} className="gap-1.5 text-xs text-gray-500 hover:text-blue-600" title="Auto-listen to this agent's next calls">
                                          <Eye className="h-3.5 w-3.5" />
                                          Follow
                                        </Button>
                                      )}
                                      <Button size="sm" variant="destructive" onClick={stopSession} className="gap-1.5">
                                        <PhoneOff className="h-3.5 w-3.5" />
                                        Stop
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => startSession(call, "listen")}
                                        disabled={isListening || isStarting}
                                        className="gap-1.5 hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
                                        title="Silent monitoring — the agent is not notified"
                                      >
                                        <Headphones className="h-3.5 w-3.5" />
                                        Listen
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => startSession(call, "whisper")}
                                        disabled={isListening || isStarting}
                                        className="gap-1.5 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                                        title="Talk to the agent only — the lead cannot hear you"
                                      >
                                        <Mic className="h-3.5 w-3.5" />
                                        Whisper
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Monitor rail (sticky) ─────────────────────────────────────────── */}
        {isListening && (
          <aside className="lg:sticky lg:top-4 lg:self-start">
            <div className={`rounded-xl border-2 p-4 ${isWhisper ? "border-blue-300 bg-blue-50/60" : "border-primary/30 bg-primary/5"}`}>
              {/* Status */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="relative">
                    <div className={`flex h-9 w-9 items-center justify-center rounded-full ${isWhisper ? "bg-blue-200" : "bg-primary/20"}`}>
                      {isWhisper ? <Mic className="h-4 w-4 text-blue-700" /> : <Headphones className="h-4 w-4 text-primary" />}
                    </div>
                    <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 animate-pulse rounded-full border-2 border-white bg-red-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-900">
                      {isWhisper ? "Whispering to" : "Listening to"} {agentName}
                    </p>
                    <p className="truncate text-xs text-gray-500">with {leadName}</p>
                  </div>
                </div>
                <span className="shrink-0 font-mono text-sm tabular-nums text-gray-600">
                  {formatDuration(listenDuration)}
                </span>
              </div>

              {/* Live spectrum — canvas + rAF reading the analyser directly. */}
              <div className="mt-3 overflow-hidden rounded-md bg-white/70 px-2 py-1.5">
                <CoachWaveform
                  readSpectrum={readSpectrum}
                  variant={isWhisper ? "whisper" : "listen"}
                  className="block h-12 w-full"
                />
              </div>

              {/* Stealth / notified disclosure — answers "does the agent know?" */}
              <div className={`mt-3 flex items-start gap-1.5 rounded-md px-2 py-1.5 text-[11px] ${isWhisper ? "bg-blue-100/70 text-blue-800" : "bg-gray-100 text-gray-600"}`}>
                {isWhisper ? <Mic className="mt-px h-3 w-3 shrink-0" /> : <EyeOff className="mt-px h-3 w-3 shrink-0" />}
                <span>
                  {isWhisper
                    ? "The agent is notified and hears you. The lead cannot hear you."
                    : "Silent — the agent is not notified that you are listening."}
                </span>
              </div>

              {/* Volume + output/mic device pickers (shared with the widget so the
                  two surfaces can't drift apart). */}
              <div className="mt-3">
                <CoachAudioControls showMic={isWhisper} />
              </div>

              {/* Push-to-talk — whisper only */}
              {isWhisper && (
                <div className="mt-3">
                  {/* CLICK-TO-TALK LATCH (2026-07-25). Was hold-to-talk only,
                      which pinned the mouse down for the whole sentence — you
                      couldn't scroll the roster, open the lead, or type a note
                      while coaching. Clicking now latches the mic on and off.
                      Spacebar stays MOMENTARY (hold) for quick interjections,
                      so both habits work. */}
                  <button
                    onClick={toggleMic}
                    aria-pressed={micActive}
                    className={`w-full select-none rounded-lg px-4 py-3 text-sm font-semibold transition-all ${
                      micActive
                        ? "bg-blue-600 text-white shadow-inner ring-2 ring-blue-300"
                        : "bg-white text-blue-700 ring-1 ring-inset ring-blue-300 hover:bg-blue-50"
                    }`}
                  >
                    {micActive ? "🔴 Mic live — click to mute" : "Click to talk"}
                  </button>
                  {/* Mic level */}
                  <div className="mt-2 h-1 overflow-hidden rounded-full bg-gray-200">
                    <div
                      className="h-full rounded-full bg-blue-500 transition-[width] duration-75"
                      style={{ width: `${Math.min(100, micVolume * 100)}%` }}
                    />
                  </div>
                  <p className="mt-1.5 text-center text-[10px] text-gray-500">
                    stays on until you click again · or hold{" "}
                    <kbd className="rounded border border-gray-300 bg-white px-1 font-mono">Space</kbd>{" "}
                    to talk briefly
                  </p>
                </div>
              )}

              {/* Session controls */}
              <div className="mt-3 flex items-center gap-2">
                {followingAgentId && (
                  <Badge variant="outline" className="border-blue-200 bg-blue-50 text-[10px] text-blue-600">
                    <Eye className="mr-1 h-2.5 w-2.5" />
                    Auto-follow
                  </Badge>
                )}
                <Button size="sm" variant="destructive" onClick={stopSession} className="ml-auto gap-1.5">
                  <PhoneOff className="h-3.5 w-3.5" />
                  Stop
                </Button>
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
